// src/screens/BookingOps.js — Booking Operations console (June 2026).
//
// Duffel-lens operations view over public.bookings: flight + hotel (Stays)
// orders, payment state, orphaned payments (charged on Stripe but no Duffel
// order), cancellations, and live admin actions (Duffel cancel + Stripe
// refund, Stripe-only refund for orphans). This is the operational lens the
// legacy email-booking view in Partners.js no longer provides.
//
// Data:    supabase.rpc('admin_booking_ops_stats')  — rollup (SECURITY DEFINER)
//          supabase.rpc('admin_get_booking_ops')    — order list w/ user email
// Actions: POST /api/booking-cancel  — Duffel cancel (+ coupled Stripe refund)
//          POST /api/booking-refund  — Stripe-only refund (orphans / goodwill)
// Both routes verify the caller's Supabase JWT server-side and log to
// activity_log.

import React, { useState, useEffect, useCallback } from 'react';
import {
  ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Pill, Table,
  Drawer, ExportButton, SkeletonRows, StackedArea, useConsoleTheme, useIsMobile, F,
} from '../components/Console';
import { supabase } from '../supabaseClient';
import { getUserRole } from '../roles';
import { setFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const fmtMoney = (n, cur) => {
  if (n == null) return '—';
  const c = (cur || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(Number(n));
  } catch (_e) { return c + ' ' + Number(n).toLocaleString(); }
};
const day = s => (s ? String(s).slice(0, 10) : '—');

const PAID_STATES = ['paid', 'succeeded', 'captured', 'complete', 'completed'];

function typeBucket(bt) {
  const s = (bt || '').toLowerCase();
  if (s.indexOf('flight') !== -1) return 'flight';
  if (/hotel|stay/.test(s)) return 'stay';
  return 'other';
}
const TYPE_ICON = { flight: '✈', stay: '⌂', other: '◆' };

function payTone(ps) {
  const s = (ps || '').toLowerCase();
  if (PAID_STATES.indexOf(s) !== -1) return 'up';
  if (s === 'refunded' || s === 'partially_refunded') return 'warn';
  if (s === 'failed' || s === 'requires_action') return 'danger';
  return undefined;
}
function statusTone(st) {
  const s = (st || '').toLowerCase();
  if (s === 'confirmed' || s === 'completed') return 'up';
  if (s === 'cancelled' || s === 'declined' || s === 'failed') return 'danger';
  if (s === 'pending' || s === 'processing' || s === 'capture_pending') return 'warn';
  return undefined;
}
function isOrphan(b) {
  return !!b.stripe_payment_id && !b.duffel_order_id
    && PAID_STATES.indexOf((b.payment_status || '').toLowerCase()) !== -1
    && !b.cancelled_at;
}

function CopyId({ label, value }) {
  const { t } = useConsoleTheme();
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = () => {
    try {
      navigator.clipboard.writeText(String(value));
      setDone(true);
      setTimeout(() => setDone(false), 1400);
    } catch (_e) {}
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0' }}>
      <span style={{ fontSize: '0.92rem', color: t.mut, flexShrink: 0 }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <code style={{ fontSize: '0.76rem', fontFamily: 'monospace', color: t.tx, background: t.codebg, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{String(value)}</code>
        <button onClick={copy} title="Copy" style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 5, cursor: 'pointer', border: `1px solid ${t.bd}`, background: t.period, color: done ? t.up : t.mut, fontFamily: F }}>{done ? '✓' : 'copy'}</button>
      </span>
    </div>
  );
}

function BookingInspector({ b, canFinancial, adminEmail, onActioned, onNav }) {
  const { t } = useConsoleTheme();
  const [working, setWorking] = useState(null);       // 'cancel' | 'refund'
  const [confirming, setConfirming] = useState(null); // which confirm strip is open
  const [refundReason, setRefundReason] = useState('');
  const [msg, setMsg] = useState(null);               // { ok, text }

  const bucket = typeBucket(b.booking_type);
  const orphan = isOrphan(b);
  const canCancel = !!b.duffel_order_id && !b.cancelled_at && (b.status || '').toLowerCase() !== 'cancelled';
  const canRefund = !!b.stripe_payment_id && (b.payment_status || '').toLowerCase() !== 'refunded';

  const act = async (kind) => {
    setWorking(kind); setMsg(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess && sess.session && sess.session.access_token;
      const url = kind === 'cancel' ? '/api/booking-cancel' : '/api/booking-refund';
      const body = kind === 'cancel'
        ? { bookingId: b.id, adminEmail }
        : { bookingId: b.id, adminEmail, reason: refundReason.trim() };
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (jwt || '') },
        body: JSON.stringify(body),
      });
      let d = {};
      try { d = await resp.json(); } catch (_e) { throw new Error('Non-JSON response — is the API route deployed?'); }
      if (!resp.ok) throw new Error(d.error || ('HTTP ' + resp.status));
      setMsg({
        ok: true,
        text: kind === 'cancel'
          ? ('Order cancelled at Duffel.' + (d.refund_amount != null ? ' Stripe refund: ' + fmtMoney(d.refund_amount, d.refund_currency) + '.' : ' No Stripe payment attached — nothing refunded.'))
          : ('Stripe refund issued: ' + fmtMoney(d.refund_amount, d.refund_currency) + (d.refund_id ? ' · ' + d.refund_id : '')),
      });
      setConfirming(null); setRefundReason('');
      if (onActioned) onActioned();
    } catch (_e) {
      setMsg({ ok: false, text: (kind === 'cancel' ? 'Cancel failed: ' : 'Refund failed: ') + _e.message });
    }
    setWorking(null);
  };

  const btn = (bg, disabled) => ({
    fontSize: '0.86rem', padding: '7px 13px', borderRadius: 7, border: 'none', fontWeight: 600, fontFamily: F,
    background: disabled ? t.gbar : bg, color: disabled ? t.mut : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const ghostBtn = { fontSize: '0.86rem', padding: '7px 13px', borderRadius: 7, fontWeight: 500, fontFamily: F, background: t.period, color: t.tx, border: `1px solid ${t.bd}`, cursor: 'pointer' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '1.3rem' }}>{TYPE_ICON[bucket]}</span>
        <span style={{ fontSize: '1.1rem', fontWeight: 600, color: t.tx }}>{b.booking_ref || ('#' + b.id)}</span>
        <Pill tone={statusTone(b.status)}>{b.status || 'unknown'}</Pill>
        {orphan && <Pill tone="danger">orphaned payment</Pill>}
      </div>
      <div style={{ fontSize: '0.88rem', color: t.mut, marginBottom: 12 }}>{b.email || (b.user_id ? b.user_id.slice(0, 8) + '…' : 'no user')}</div>

      {orphan && (
        <div style={{ background: t.alert, border: `1px solid ${t.alertbd}`, color: t.alertc, borderRadius: 8, padding: '8px 12px', fontSize: '0.9rem', marginBottom: 12, lineHeight: 1.45 }}>
          ⚠ Stripe shows a charge but no Duffel order was created. Refund the payment below, or finish the order manually in the Duffel dashboard.
        </div>
      )}

      <StatLine label="Type" value={b.booking_type || '—'} />
      <StatLine label="Source" value={b.booking_source || '—'} />
      <StatLine label="Payment" value={b.payment_status || '—'} color={payTone(b.payment_status) === 'up' ? t.up : payTone(b.payment_status) === 'danger' ? t.danger : undefined} />
      <StatLine label="Value" value={fmtMoney(b.total_amount != null ? b.total_amount : b.quoted_price, b.currency || b.quoted_currency)} />
      {b.passenger_count != null && <StatLine label="Passengers" value={fmt(b.passenger_count)} />}
      {b.venue_name && <StatLine label="Property / venue" value={b.venue_name} />}
      <StatLine label="Created" value={day(b.created_at)} />
      {b.confirmed_at && <StatLine label="Confirmed" value={day(b.confirmed_at)} />}
      {b.cancelled_at && <StatLine label="Cancelled" value={day(b.cancelled_at)} color={t.danger} />}

      <div style={{ height: 1, background: t.bd, margin: '12px 0' }} />
      <CopyId label="Duffel order" value={b.duffel_order_id} />
      <CopyId label="Duffel offer" value={b.duffel_offer_id} />
      <CopyId label="Stripe payment" value={b.stripe_payment_id} />
      <CopyId label="Trip" value={b.trip_id} />
      <CopyId label="User ID" value={b.user_id} />
      {b.ticket_pdf_url && (
        <div style={{ padding: '6px 0' }}>
          <a href={b.ticket_pdf_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.92rem', color: t.accent }}>Ticket PDF ↗</a>
        </div>
      )}
      {b.user_id && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => { setFocus({ type: 'user', value: b.user_id }); if (onNav) onNav('users'); }}
            style={{ fontSize: '0.84rem', padding: '6px 12px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${t.bd}`, background: t.period, color: t.tx, fontWeight: 500, fontFamily: F }}>
            Open user →
          </button>
        </div>
      )}

      <div style={{ height: 1, background: t.bd, margin: '12px 0' }} />
      <div style={{ fontSize: '0.74rem', color: t.mut, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>Actions</div>

      {!canFinancial && <Empty>Your role is read-only for financial actions. Ask an admin to cancel or refund.</Empty>}

      {canFinancial && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <button onClick={() => setConfirming(confirming === 'cancel' ? null : 'cancel')} disabled={!canCancel || working !== null} style={btn(t.danger, !canCancel || working !== null)}>
              {working === 'cancel' ? 'Cancelling…' : 'Cancel order at Duffel'}
            </button>
            {!canCancel && (
              <div style={{ fontSize: '0.78rem', color: t.mut, marginTop: 4 }}>
                {b.cancelled_at || (b.status || '').toLowerCase() === 'cancelled' ? 'Already cancelled.' : 'No Duffel order attached — nothing to cancel at Duffel.'}
              </div>
            )}
            {confirming === 'cancel' && (
              <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.bd}`, background: t.rowbg }}>
                <div style={{ fontSize: '0.88rem', color: t.tx, marginBottom: 8, lineHeight: 1.5 }}>
                  Irreversibly cancels the live {bucket === 'stay' ? 'hotel booking' : 'flight order'} at Duffel, then refunds the Stripe payment in full. Continue?
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => act('cancel')} disabled={working !== null} style={btn(t.danger, working !== null)}>Yes — cancel & refund</button>
                  <button onClick={() => setConfirming(null)} style={ghostBtn}>Keep order</button>
                </div>
              </div>
            )}
          </div>
          <div>
            <button onClick={() => setConfirming(confirming === 'refund' ? null : 'refund')} disabled={!canRefund || working !== null} style={btn('#9F7E4F', !canRefund || working !== null)}>
              {working === 'refund' ? 'Refunding…' : 'Refund payment only (Stripe)'}
            </button>
            {!canRefund && (
              <div style={{ fontSize: '0.78rem', color: t.mut, marginTop: 4 }}>
                {(b.payment_status || '').toLowerCase() === 'refunded' ? 'Already refunded.' : 'No Stripe payment attached.'}
              </div>
            )}
            {confirming === 'refund' && (
              <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.bd}`, background: t.rowbg }}>
                <div style={{ fontSize: '0.88rem', color: t.tx, marginBottom: 6, lineHeight: 1.5 }}>
                  Full Stripe refund without touching Duffel — use for orphaned payments or goodwill. Reason (required):
                </div>
                <input value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="e.g. orphaned payment — charge without Duffel order"
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.9rem', padding: '6px 9px', borderRadius: 6, border: `1px solid ${t.bd}`, background: t.panel, color: t.tx, outline: 'none', fontFamily: F, marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => act('refund')} disabled={!refundReason.trim() || working !== null} style={btn('#9F7E4F', !refundReason.trim() || working !== null)}>Issue refund</button>
                  <button onClick={() => setConfirming(null)} style={ghostBtn}>Back</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {msg && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.45,
          background: msg.ok ? t.live : t.alert, color: msg.ok ? t.livec : t.alertc, border: `1px solid ${msg.ok ? t.bd : t.alertbd}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function BookingOpsBody({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [fType, setFType] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [adminEmail, setAdminEmail] = useState('');
  const [myRole, setMyRole] = useState('support');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const email = (data && data.user && data.user.email) || '';
      setAdminEmail(email);
      setMyRole(getUserRole(email));
    });
  }, []);
  const canFinancial = myRole === 'admin';

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        supabase.rpc('admin_booking_ops_stats'),
        supabase.rpc('admin_get_booking_ops', { p_limit: 500 }),
      ]);
      setStats(s.data || {});
      setRows(Array.isArray(l.data) ? l.data : []);
    } catch (_e) { setStats({}); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const loading = rows == null;
  const all = rows || [];
  const orphanCount = all.filter(isOrphan).length;

  let view = all;
  if (fType !== 'all') view = view.filter(r => typeBucket(r.booking_type) === fType);
  if (fStatus === 'orphan') view = view.filter(isOrphan);
  else if (fStatus !== 'all') view = view.filter(r => (r.status || '').toLowerCase() === fStatus);

  const statuses = Array.from(new Set(all.map(r => (r.status || '').toLowerCase()).filter(Boolean))).sort();

  const s = stats || {};
  const byType = s.by_type || [];
  const byPayment = s.by_payment || [];
  const series = s.series || [];
  const failures = s.recent_failures || [];
  const maxType = Math.max(1, ...byType.map(b => b.n || 0));
  const maxPay = Math.max(1, ...byPayment.map(b => b.n || 0));
  const col = n => (mobile ? (n >= 4 ? '1fr 1fr' : '1fr') : `repeat(${n},1fr)`);

  const filterBtn = active => ({
    fontSize: '0.85rem', padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: F, fontWeight: 500,
    border: `1px solid ${active ? t.accent : t.bd}`, background: active ? t.accent + '22' : t.period, color: active ? t.accent : t.mut,
  });

  const cols = [
    { label: 'Ref', render: r => r.booking_ref || ('#' + r.id), mono: true, sort: r => r.booking_ref || String(r.id), text: r => r.booking_ref || String(r.id) },
    { label: 'Type', render: r => (TYPE_ICON[typeBucket(r.booking_type)] + ' ' + (r.booking_type || '—')), sort: r => r.booking_type || '', text: r => r.booking_type || '' },
    { label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{r.status || '—'}</Pill>, sort: r => r.status || '', text: r => r.status || '' },
    { label: 'Payment', render: r => (isOrphan(r) ? <Pill tone="danger">orphaned</Pill> : <Pill tone={payTone(r.payment_status)}>{r.payment_status || '—'}</Pill>), sort: r => r.payment_status || '', text: r => (isOrphan(r) ? 'orphaned' : (r.payment_status || '')) },
    { label: 'Value', render: r => fmtMoney(r.total_amount != null ? r.total_amount : r.quoted_price, r.currency || r.quoted_currency), align: 'right', num: true, sort: r => Number(r.total_amount != null ? r.total_amount : r.quoted_price) || 0, text: r => String(r.total_amount != null ? r.total_amount : (r.quoted_price || '')) },
    { label: 'User', render: r => (r.email ? (r.email.length > 24 ? r.email.slice(0, 22) + '…' : r.email) : '—'), muted: true, sort: r => r.email || '', text: r => r.email || '' },
    { label: 'Duffel ID', render: r => (r.duffel_order_id ? r.duffel_order_id.slice(0, 14) + '…' : '—'), mono: true, muted: true, text: r => r.duffel_order_id || '' },
    { label: 'Created', render: r => day(r.created_at), muted: true, sort: r => r.created_at || '', text: r => day(r.created_at) },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: col(6), gap: 10 }}>
        <KPI label="Bookings · all-time" value={loading ? '…' : fmt(s.total)} sub={loading ? undefined : (fmt(s.last7) + ' in last 7d')} accent="#17a86a" />
        <KPI label="Bookings · 30d" value={loading ? '…' : fmt(s.last30)} sub="created" accent={t.accent} />
        <KPI label="Paid value · 30d" value={loading ? '…' : fmtMoney(s.value30, 'USD')} sub="sum, native currencies" accent="#314F5C" />
        <KPI label="Orphaned payments" value={loading ? '…' : fmt(s.orphans)} sub={s.orphans > 0 ? 'needs action now' : 'charged, no order'} subColor={s.orphans > 0 ? t.danger : t.mut} accent={s.orphans > 0 ? '#dc3545' : '#6F8FA7'} onClick={() => { setFStatus('orphan'); setFType('all'); }} />
        <KPI label="Cancelled · 30d" value={loading ? '…' : fmt(s.cancelled30)} sub="any source" accent="#fd7e14" />
        <KPI label="Refunded" value={loading ? '…' : fmt(s.refunded)} sub="payments, all-time" accent="#9F7E4F" />
      </div>

      {!loading && s.orphans > 0 && (
        <div style={{ marginTop: 12, background: t.alert, border: `1px solid ${t.alertbd}`, color: t.alertc, borderRadius: 10, padding: '10px 14px', fontSize: '0.95rem', lineHeight: 1.5 }}>
          ⚠ <b>{fmt(s.orphans)} orphaned payment{s.orphans === 1 ? '' : 's'}</b> — Stripe charged the customer but no Duffel order exists. Open each one below and either refund or complete the order in Duffel.
        </div>
      )}

      {!loading && (s.cancel_failures || 0) > 0 && (
        <Panel title="⚠ Cancellation reconciliation failures" right={fmt(s.cancel_failures) + ' total'} style={{ marginTop: 12, borderColor: t.alertbd }}>
          {failures.map((f2, i) => (
            <div key={i} style={{ fontSize: '0.88rem', color: t.tx, padding: '5px 0', borderBottom: `.5px solid ${t.bd}` }}>
              <span style={{ fontFamily: 'monospace', color: t.mut }}>#{f2.booking_id}</span>
              {' '}<Pill tone="danger">{f2.stage || 'unknown stage'}</Pill>{' '}
              <span style={{ color: t.mut }}>{day(f2.at)}</span>
              <div style={{ color: t.danger, fontSize: '0.8rem', marginTop: 2 }}>{f2.error}</div>
            </div>
          ))}
        </Panel>
      )}

      <SectionLabel right="last 14 days">Order flow</SectionLabel>
      <Panel>
        <StackedArea data={series} height={170} keys={[
          { key: 'flights', label: 'Flights', color: '#6FBFE0' },
          { key: 'stays', label: 'Hotels', color: '#5dcaa5' },
          { key: 'other', label: 'Other', color: '#9F7E4F' },
        ]} />
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Panel title="✈ Orders by type">
          {byType.length === 0
            ? <Empty>No bookings yet — the breakdown fills in once the first Duffel order lands.</Empty>
            : byType.map(b2 => <Bar key={b2.type} label={b2.type} value={fmt(b2.n)} pct={Math.round((b2.n / maxType) * 100)} labelWidth={110} hint={b2.type + ': ' + fmt(b2.n) + ' orders · value ' + fmt(b2.value)} />)}
        </Panel>
        <Panel title="◍ Payment states">
          {byPayment.length === 0
            ? <Empty>No payment activity yet.</Empty>
            : byPayment.map(p => <Bar key={p.status} label={p.status} value={fmt(p.n)} pct={Math.round((p.n / maxPay) * 100)} labelWidth={110} color={(p.status || '').toLowerCase() === 'refunded' ? '#9F7E4F' : undefined} />)}
        </Panel>
      </div>

      <SectionLabel right={loading ? '' : (fmt(view.length) + ' of ' + fmt(all.length) + ' orders')}>Orders</SectionLabel>
      <Panel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
          {[['all', 'All'], ['flight', '✈ Flights'], ['stay', '⌂ Hotels'], ['other', '◆ Other']].map(([k, lbl]) => (
            <button key={k} onClick={() => setFType(k)} style={filterBtn(fType === k)}>{lbl}</button>
          ))}
          <span style={{ width: 1, height: 20, background: t.bd, margin: '0 4px' }} />
          <button onClick={() => setFStatus('all')} style={filterBtn(fStatus === 'all')}>Any status</button>
          <button onClick={() => setFStatus('orphan')} style={filterBtn(fStatus === 'orphan')}>⚠ Orphaned{orphanCount ? ' (' + orphanCount + ')' : ''}</button>
          {statuses.map(st => <button key={st} onClick={() => setFStatus(st)} style={filterBtn(fStatus === st)}>{st}</button>)}
          <span style={{ marginLeft: 'auto' }}><ExportButton filename="sanoire-bookings" cols={cols} rows={view} /></span>
        </div>
        {loading ? <SkeletonRows n={5} /> : (
          <Table
            cols={cols}
            rows={view}
            empty={all.length === 0
              ? 'No bookings yet. Orders appear here the moment the first Duffel flight or hotel booking is created in the app.'
              : 'No orders match this filter.'}
            onRowClick={r => setSel(r)}
            searchable
          />
        )}
      </Panel>

      <Drawer open={!!sel} onClose={() => setSel(null)} title="Booking" width={420}>
        {sel && <BookingInspector b={sel} canFinancial={canFinancial} adminEmail={adminEmail} onActioned={load} onNav={onNav} />}
      </Drawer>
    </div>
  );
}

export default function BookingOps(props) {
  return (
    <ConsoleShell title="Booking Operations">
      <BookingOpsBody onNav={props.onNav} />
    </ConsoleShell>
  );
}
